<template>
    <form :action="url" method="post" @submit.prevent="submit">
        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.title')" for="title" />
                        <FormInput
                            :placeholder="$t('messages.(optional)')"
                            id="title"
                            v-model="form.title"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.title" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            required
                            id="group_id"
                            v-model.number="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        <FormSelect
                            required
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="filteredSubjects"
                            class="mt-1 w-full"
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : ` ${$t('messages.select_X', { X: $t('messages.group') })}`
                            "
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.published_at')" for="published_at" />
                        <FormInput
                            id="published_at"
                            v-model="form.published_at"
                            name="published_at"
                            type="datetime-local"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.published_at" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.unpublished_at')" for="unpublished_at" />
                        <FormInput
                            id="unpublished_at"
                            v-model="form.unpublished_at"
                            name="unpublished_at"
                            type="datetime-local"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.unpublished_at" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>
        <TitleFieldsFormSection>
            <template v-slot:title>
                <div>
                    {{ $t('messages.activity_mode') }}
                </div>
                <div class="mt-3">
                    <AlertMessage class="mt-5 inline-block" type="info">
                        <template v-slot:title>
                            {{ $t('messages.information') }}
                        </template>
                        <template v-slot:body>
                            {{ $t('messages.practice_mode_explanation') }}
                        </template>
                    </AlertMessage>
                </div>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.activity_mode')" for="mode" />
                        <div>
                            <FormRadioGroupCards name="mode" v-model="form.mode" :options="options.modes" />
                        </div>

                        <FormError :value="form.errors.mode" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>
        <TitleFieldsFormSection>
            <template v-slot:title>
                <div>
                    {{ $t('messages.caution') }}
                </div>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.should_exclude_when_time_away')"
                            for="should_exclude_when_time_away"
                        />
                        <input
                            id="should_exclude_when_time_away"
                            type="checkbox"
                            v-model="form.should_exclude_when_time_away"
                            name="where_has_grades_of_group"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.should_exclude_when_time_away" class="mt-2" />
                    </div>
                    <div>
                        <AlertMessage class="mt-5 inline-block" type="info">
                            <template v-slot:title>
                                {{ $t('messages.information') }}
                            </template>
                            <template v-slot:body>
                                {{ $t('messages.should_exclude_when_time_away_teacher_explanation') }}
                            </template>
                        </AlertMessage>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <PageSectionCollapsible class="mt-5">
            <template v-slot:title> {{ $tc('messages.tags', 2) }} ({{ form.tag_ids.length }}) </template>
            <div v-if="!form.subject_id">
                {{ ` ${$t('messages.select_X', { X: $tc('messages.subject', 1) })}` }}
            </div>
            <QuestionTagSelectorByBlock v-model="form.tag_ids" :blocks="contentBlocks" />

            <template v-slot:closed>
                <div class="mt-3">
                    <div v-if="!form.subject_id">
                        {{ ` ${$t('messages.select_X', { X: $tc('messages.subject', 1) })}` }}
                    </div>
                    <AppCard>
                        <div class="p-2 flex flex-wrap gap-2">
                            <BadgePill color="indigo" v-for="tag in selectedTags" :key="tag.id">
                                {{ tag.name }}
                            </BadgePill>
                        </div>
                    </AppCard>
                </div>
            </template>
        </PageSectionCollapsible>

        <PageSectionCollapsible class="mt-5">
            <template v-slot:title>
                {{ $tc('messages.students', 2) }} ({{ form.student_ids.length }} / {{ students.length }})
            </template>
            <div v-if="!form.group_id">
                {{ ` ${$t('messages.select_X', { X: $t('messages.group') })}` }}
            </div>

            <AppCard>
                <div class="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 p-2">
                    <label v-for="student in students" :key="student.value">
                        <input
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            name="student_ids[]"
                            :value="student.value"
                            v-model="form.student_ids"
                        />
                        <BadgePill :color="form.student_ids.includes(student.value) ? 'indigo' : 'gray'">
                            {{ student.text }}
                        </BadgePill>
                    </label>
                </div>
            </AppCard>
        </PageSectionCollapsible>

        <div class="flex flex-w justify-end gap-2 items-bottom mt-5">
            <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                {{ $t('messages.save') }}
                <span v-if="form.processing" class="ml-1 btn-spinner"></span>
            </AppButton>
        </div>
    </form>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import FormRadioGroupCards from '@/components/Forms/FormRadioGroupCards.vue';
import axios from 'axios';
import PageSectionCollapsible from '../../../../Shared/PageSectionCollapsible.vue';
import QuestionTagSelectorByBlock from '../../../../components/QuestionTagSelectorByBlock.vue';
import BadgePill from '../../../../components/BadgePill.vue';
import AppCard from '../../../../Shared/AppCard.vue';
import AlertMessage from '../../../../components/AlertMessage.vue';

export default {
    components: {
        AlertMessage,
        AppCard,
        BadgePill,
        QuestionTagSelectorByBlock,
        PageSectionCollapsible,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        AppButton,
        FormRadioGroupCards,
    },
    layout: Layout,
    name: 'Create',
    props: {
        groupPractice: {
            type: Object,
            required: true,
        },
        meta: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        url: {
            type: String,
            required: true,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: this.isEdit ? 'patch' : 'post',
                title: this.groupPractice.title,
                mode: this.groupPractice.mode,
                group_id: this.groupPractice.group_id,
                subject_id: this.groupPractice.subject_id,
                minutes: this.groupPractice.minutes,
                published_at: this.groupPractice.published_at,
                unpublished_at: this.groupPractice.unpublished_at,
                should_exclude_when_time_away: this.groupPractice.should_exclude_when_time_away,
                tag_ids: this.groupPractice.tag_ids,
                student_ids: this.groupPractice.student_ids,
            }),
            contentBlocks: this.options.blocks,
            students: this.options.students,
        };
    },
    methods: {
        submit() {
            this.form.post(this.url, {
                preserveScroll: true,
            });
        },
        fetchStudents(groupId) {
            axios.get(this.route('select.group_students.show', { group: groupId })).then((response) => {
                this.form.student_ids = [];
                this.students = response.data.items;
                this.includeStudents();
            });
        },
        includeStudents() {
            this.form.student_ids = this.students.map((s) => s.value);
        },
    },
    mounted() {
        if (!this.isEdit && this.form.student_ids.length === 0) {
            this.includeStudents();
        }
    },
    computed: {
        stage_id: function () {
            if (this.form.group_id === null) {
                console.log('this.form.group_id === null');
                return null;
            }
            const group = this.options.groups.find((g) => g.value === +this.form.group_id);

            if (group === undefined) {
                return null;
            }
            return group.stage_id;
        },
        filteredSubjects: function () {
            console.log('computed filteredSubjects');
            if (this.stage_id === null) {
                return [];
            }

            return this.options.subjects.filter((s) => s.stage_id == this.stage_id);
        },
        selectedTags: function () {
            return this.contentBlocks.flatMap((b) => b.tags).filter((t) => this.form.tag_ids.includes(t.id));
        },
    },
    watch: {
        stage_id: function (oldId, newId) {
            if (oldId === null) {
                return;
            }
            if (newId === null) {
                return;
            }
            this.form.subject_id = null;
        },
        'form.subject_id': {
            handler(subjectId) {
                if (subjectId === '' || subjectId === null) {
                    this.form.tag_ids = [];
                    this.contentBlocks = [];
                } else {
                    axios
                        .get(this.meta.links.subject_tags_checkboxes, {
                            params: { subject_id: subjectId },
                        })
                        .then((response) => {
                            this.contentBlocks = response.data.items.blocks;
                        });
                }
                console.log(subjectId);
            },
            deep: true,
        },
        'form.group_id': {
            handler(groupId) {
                this.fetchStudents(groupId);
            },
            deep: true,
        },
    },
};
</script>
